import React, { useState } from 'react';
import { useAxios } from '../../Helpers/AxiosHelper';
import { useForm } from 'react-hook-form';
import { useToast } from '../../Helpers/ToastHelper';
import { useTranslation } from 'react-i18next';
import { classNames } from 'primereact/utils';
import { Card, ProgressSpinner, InputText, FloatLabel, SCButton, DDCountries, Controller } from '../../Common/Imports';
import { ApiResponse, Stakeholder_EXT } from '../../Models/Classes';

export const StakeholderNew: React.FC = () => {
    const { t } = useTranslation();
    const { control, handleSubmit, reset } = useForm<Stakeholder_EXT>();
    const AxiosHelper = useAxios();
    const { showToast } = useToast();
    const [showLoader, setShowloader] = useState<boolean>(false);

    const onSubmit = async (formData: Stakeholder_EXT) => {
        setShowloader(true);
        try {
            const resp = await AxiosHelper.post<ApiResponse<Stakeholder_EXT>>(`Stakeholder/CreateStakeholder`, formData);
            if (resp.data.code === 0) {
                showToast('success', 'Success', t("Common.Toasts.New.Success"));
                reset(); // Reset the form after successful submission
            } else {
                showToast('error', 'Error', t("Common.Toasts.New.Error"));
            }
        } catch {
            showToast('error', 'Error', t("Common.Toasts.New.Error"));
        } finally {
            setShowloader(false);
        }
    };

    const footerBody = () => {
        return (
            <div className="row">
                <div className="col d-flex justify-content-end">
                    <SCButton Name="Save" Label={t("Common.Buttons.Save")} OnClick={handleSubmit(onSubmit)} />
                </div>
            </div>
        );
    };

    const renderInputField = (
        name: keyof Stakeholder_EXT,
        label: string,
        requiredMessage: string,
        type: string = 'text'
    ) => (
        <Controller
            name={name}
            control={control}  // Pass the control object here
            rules={{ required: requiredMessage }}
            render={({ field, fieldState }) => (
                <div className="mb-4">
                    <FloatLabel>
                        <InputText
                            id={name}
                            {...field}
                            type={type}
                            value={field.value ? String(field.value) : ''}  // Ensure value is a string
                            className={classNames({ 'p-invalid': fieldState.invalid })}
                        />
                        <label htmlFor={name}>{label}</label>
                    </FloatLabel>
                    {fieldState.error && (
                        <small className="p-error">
                            {fieldState.error.message}
                        </small>
                    )}
                </div>
            )}
        />
    );

    return (
        showLoader ?
            <ProgressSpinner />
            :
            <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
                <Card className="mb-3" footer={footerBody}>
                    <div className="row">
                        <div className="col-md-12 mt-3">
                            {renderInputField(
                                'name',
                                t("Stakeholders.Name"),
                                `${t("Stakeholders.Name")} ${t("Common.Form.Required")}`
                            )}
                        </div>
                        <div className="col-md-12">
                            {renderInputField(
                                'address',
                                t("Stakeholders.Address"),
                                `${t("Stakeholders.Address")} ${t("Common.Form.Required")}`
                            )}
                        </div>
                        <div className="col-md-3">
                            {renderInputField(
                                'city',
                                t("Stakeholders.City"),
                                `${t("Stakeholders.City")} ${t("Common.Form.Required")}`
                            )}
                        </div>
                        <div className="col-md-3">
                            {renderInputField(
                                'region',
                                t("Stakeholders.Region"),
                                `${t("Stakeholders.Region")} ${t("Common.Form.Required")}`
                            )}
                        </div>
                        <div className="col-md-3">
                            <Controller
                                name="idCountry"
                                control={control}
                                rules={{ required: `${t("Stakeholders.Country")} ${t("Common.Form.Required")}` }}
                                render={({ field, fieldState }) => (
                                    <div className="mb-4">
                                        <DDCountries
                                            value={field.value}
                                            onChange={(value) => field.onChange(value)}
                                            onBlur={field.onBlur}
                                            valid={fieldState.invalid}
                                        />
                                        {fieldState.invalid && (
                                            <small className="p-error">
                                                {fieldState.error?.message}
                                            </small>
                                        )}
                                    </div>
                                )}
                            />
                        </div>
                        <div className="col-md-3 ">
                            {renderInputField(
                                'zip',
                                t("Stakeholders.ZIP"),
                                `${t("Stakeholders.ZIP")} ${t("Common.Form.Required")}`
                            )}
                        </div>
                        <div className="col-md-4">
                            {renderInputField(
                                'vat',
                                t("Stakeholders.VAT"),
                                `${t("Stakeholders.VAT")} ${t("Common.Form.Required")}`
                            )}
                        </div>
                        <div className="col-md-4">
                            {renderInputField(
                                'email',
                                t("Stakeholders.Email"),
                                `${t("Stakeholders.Email")} ${t("Common.Form.Required")}`
                            )}
                        </div>
                        <div className="col-md-4">
                            {renderInputField(
                                'phone',
                                t("Stakeholders.Phone"),
                                t("Stakeholders.Phone") + " " + t("Auth.Register.Form.Required")
                            )}
                        </div>
                    </div>
                </Card>
            </form>
    );
};

export default StakeholderNew;
